import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  AttachMoney,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Receipt,
  CreditCard,
  Assessment,
  PieChart,
  Timeline,
  Download,
  Refresh,
  MonetizationOn,
  Savings,
  ShoppingCart
} from '@mui/icons-material';
import { analyticsService } from '../../../services/analyticsService';

interface DateRange {
  start: Date;
  end: Date;
}

interface FinancialReportsProps {
  dateRange: DateRange;
}

interface FinancialSummary {
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  profitMargin: number;
  grossMargin: number;
  cashFlow: number;
  outstandingInvoices: number;
  paidInvoices: number;
  overdueInvoices: number;
}

interface RevenueStream {
  id: string;
  name: string;
  amount: number;
  percentage: number;
  trend: 'up' | 'down' | 'stable';
  change: number;
  category: 'recurring' | 'one-time' | 'project-based';
}

interface Expense {
  id: string;
  category: string;
  amount: number;
  percentage: number;
  trend: 'up' | 'down' | 'stable';
  change: number;
  type: 'fixed' | 'variable' | 'one-time';
}

interface Invoice {
  id: string;
  client: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue' | 'draft';
  dueDate: Date;
  issueDate: Date;
  project: string;
  paymentMethod?: string;
}

interface FinancialMetric {
  name: string;
  current: number;
  previous: number;
  target: number;
  unit: string;
  format: 'currency' | 'percentage' | 'number';
}

const FinancialReports: React.FC<FinancialReportsProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [financialData, setFinancialData] = useState<any>(null);
  const [reportType, setReportType] = useState('overview');
  const [period, setPeriod] = useState('monthly');

  // Load financial data from API
  useEffect(() => {
    const loadFinancialReports = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await analyticsService.getFinancialReports({
          start: dateRange.start,
          end: dateRange.end
        });
        setFinancialData(data);
      } catch (err) {
        console.error('Error loading financial reports:', err);
        setError('Failed to load financial reports data');
      } finally {
        setLoading(false);
      }
    };

    loadFinancialReports();
  }, [dateRange, reportType, period]);

  // Transform API data to component format
  const financialSummary: FinancialSummary = financialData?.summary || {
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    profitMargin: 0,
    grossMargin: 0,
    cashFlow: 0,
    outstandingInvoices: 0,
    paidInvoices: 0,
    overdueInvoices: 0
  };

  const revenueStreams: RevenueStream[] = financialData?.revenue_streams?.map((stream: any) => ({
    id: stream.id,
    name: stream.name,
    amount: stream.amount,
    percentage: stream.percentage,
    trend: stream.trend,
    change: stream.change,
    category: stream.category
  })) || [];

  const expenses: Expense[] = financialData?.expenses?.map((expense: any) => ({
    id: expense.id,
    category: expense.category,
    amount: expense.amount,
    percentage: expense.percentage,
    trend: expense.trend,
    change: expense.change,
    type: expense.type
  })) || [];

  const recentInvoices: Invoice[] = financialData?.recent_invoices?.map((invoice: any) => ({
    id: invoice.id,
    client: invoice.client,
    amount: invoice.amount,
    status: invoice.status,
    dueDate: new Date(invoice.due_date),
    issueDate: new Date(invoice.issue_date),
    project: invoice.project,
    paymentMethod: invoice.payment_method
  })) || [];

  const keyMetrics: FinancialMetric[] = financialData?.key_metrics?.map((metric: any) => ({
    name: metric.name,
    current: metric.current,
    previous: metric.previous,
    target: metric.target,
    unit: metric.unit,
    format: metric.format
  })) || [];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString();
  };

  const formatValue = (value: number, format: string, unit: string) => {
    switch (format) {
      case 'currency':
        return formatCurrency(value);
      case 'percentage':
        return formatPercentage(value);
      case 'number':
        return formatNumber(value) + unit;
      default:
        return value.toString();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'error';
      case 'draft': return 'info';
      default: return 'default';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp color="success" fontSize="small" />;
      case 'down': return <TrendingDown color="error" fontSize="small" />;
      default: return null;
    }
  };

  const getTrendColor = (current: number, previous: number, isReverse = false) => {
    const isUp = current > previous;
    if (isReverse) {
      return isUp ? 'error' : 'success';
    }
    return isUp ? 'success' : 'error';
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await analyticsService.getFinancialReports({
        start: dateRange.start,
        end: dateRange.end
      });
      setFinancialData(data);
    } catch (err) {
      console.error('Error refreshing financial data:', err);
      setError('Failed to refresh financial data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      await analyticsService.exportFinancialReports('pdf', reportType);
      console.log('Financial report exported successfully');
    } catch (err) {
      console.error('Error exporting financial report:', err);
    }
  };

  // Show loading state
  if (loading && !financialData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Report Type</InputLabel>
          <Select
            value={reportType}
            label="Report Type"
            onChange={(e) => setReportType(e.target.value)}
          >
            <MenuItem value="overview">Financial Overview</MenuItem>
            <MenuItem value="revenue">Revenue Analysis</MenuItem>
            <MenuItem value="expenses">Expense Analysis</MenuItem>
            <MenuItem value="cash-flow">Cash Flow</MenuItem>
            <MenuItem value="profit-loss">P&L Statement</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={period}
            label="Period"
            onChange={(e) => setPeriod(e.target.value)}
          >
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
          onClick={handleRefresh}
          disabled={loading}
          size="small"
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>

        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleExport}
          size="small"
        >
          Export
        </Button>
      </Box>

      {/* Financial Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MonetizationOn color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Revenue</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {formatCurrency(financialSummary.totalRevenue)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                +24.5% from last period
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShoppingCart color="error" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Expenses</Typography>
              </Box>
              <Typography variant="h4" color="error.main">
                {formatCurrency(financialSummary.totalExpenses)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                +12.3% from last period
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Savings color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Net Profit</Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                {formatCurrency(financialSummary.netProfit)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatPercentage(financialSummary.profitMargin)} margin
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalance color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Cash Flow</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {formatCurrency(financialSummary.cashFlow)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Positive cash position
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Revenue Breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom>
              Revenue Streams
            </Typography>
            <List>
              {revenueStreams.map((stream) => (
                <ListItem key={stream.id} divider>
                  <ListItemIcon>
                    <PieChart color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2">{stream.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="h6" sx={{ mr: 1 }}>
                            {formatCurrency(stream.amount)}
                          </Typography>
                          {getTrendIcon(stream.trend)}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {formatPercentage(stream.percentage)} of total revenue
                        </Typography>
                        <Chip
                          size="small"
                          label={stream.category}
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Expense Breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom>
              Expense Categories
            </Typography>
            <List>
              {expenses.map((expense) => (
                <ListItem key={expense.id} divider>
                  <ListItemIcon>
                    <Receipt color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2">{expense.category}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="h6" sx={{ mr: 1 }}>
                            {formatCurrency(expense.amount)}
                          </Typography>
                          {getTrendIcon(expense.trend)}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {formatPercentage(expense.percentage)} of total expenses
                        </Typography>
                        <Chip
                          size="small"
                          label={expense.type}
                          color={expense.type === 'fixed' ? 'primary' : expense.type === 'variable' ? 'secondary' : 'default'}
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Key Financial Metrics */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Key Financial Metrics
            </Typography>
            <List>
              {keyMetrics.map((metric, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2">{metric.name}</Typography>
                        <Typography
                          variant="h6"
                          color={getTrendColor(metric.current, metric.previous, metric.name.includes('Cost') || metric.name.includes('Cycle'))}
                        >
                          {formatValue(metric.current, metric.format, metric.unit)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Previous: {formatValue(metric.previous, metric.format, metric.unit)} | 
                          Target: {formatValue(metric.target, metric.format, metric.unit)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            color={getTrendColor(metric.current, metric.previous, metric.name.includes('Cost') || metric.name.includes('Cycle'))}
                          >
                            {metric.current > metric.previous ? '+' : ''}
                            {formatPercentage(((metric.current - metric.previous) / metric.previous) * 100)}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Recent Invoices */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Invoices
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Due Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentInvoices.slice(0, 5).map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>
                        <Typography variant="body2">{invoice.client}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(invoice.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={invoice.status}
                          color={getStatusColor(invoice.status) as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.dueDate.toLocaleDateString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Financial Alerts */}
      <Box sx={{ mt: 3 }}>
        <Alert severity="warning" sx={{ mb: 1 }}>
          <strong>Outstanding Invoices:</strong> {financialSummary.overdueInvoices} invoices are overdue. Total amount: {formatCurrency(185000)}.
        </Alert>
        <Alert severity="info">
          <strong>Cash Flow Alert:</strong> Based on current projections, cash flow will remain positive for the next 6 months.
        </Alert>
      </Box>
    </Box>
  );
};

export default FinancialReports;